import React, { useState } from "react";
import { ChevronDown, Eye, PenBox } from "lucide-react";


import {
  getStatusColor,
  STATUS_OPTIONS,
} from "../../../../constants/Journalconstants";

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium break-words ${color.bg} ${color.text}`}
    >
      {status || "N/A"}
    </span>
  );
};

const Row = ({ label, children }) => (
  <div className="mt-2 text-sm">
    <span className="font-medium text-gray-500">{label}:</span>{" "}
    <span className="break-words text-gray-800">{children}</span>
  </div>
);

export default function MobJournalCard({
  journals = [],
  onView, 
  onEdit, 
  onStatusChange,
}) {
  const [openId, setOpenId] = useState(null);

  if (!journals || journals.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-gray-500">
        No journals found
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {journals.map((item) => {
        const isOpen = openId === item.id;
        const toggle = () => setOpenId(isOpen ? null : item.id);
        const journalName = item?.journal?.journalName;
        const journalLink = item?.journal?.journalLink;
        const statusColor = getStatusColor(item.status);

        return (
          <div
            key={item.id}
            onClick={toggle}
            className="relative h-full cursor-pointer rounded-md border border-[#d3cccc] bg-white p-4 pb-16 shadow-sm transition active:scale-[0.98]"
          >
            {/* Always visible (4 fields) */}
            <div className="text-sm">
              <span className="font-medium text-gray-500">Journal:</span>{" "}
              {journalLink ? (
                <a
                  href={journalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="break-words font-medium text-blue-700 hover:underline"
                >
                  {journalName || "-"}
                </a>
              ) : (
                <span className="break-words text-gray-800">
                  {journalName || "-"}
                </span>
              )}
            </div>

            <Row label="Title">
              <span className="line-clamp-2">
                {item?.journal?.document?.title ?? "-"}
              </span>
            </Row>

            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-500">Status:</span>
              {onStatusChange ? (
                <select
                  value={item.status}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onStatusChange(item.id, e.target.value)}
                  aria-label={`Status for ${journalName || "journal"}`}
                  className={`h-8 min-w-0 flex-1 rounded-md px-2 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-600 ${statusColor.text} ${statusColor.bg}`}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option
                      key={status}
                      value={status}
                      className={getStatusColor(status).text}
                    >
                      {status}
                    </option>
                  ))}
                </select>
              ) : (
                <StatusBadge status={item.status} />
              )}
            </div>

            <Row label="Last Updated">
              {item.updatedAt
                ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                : "-"}
            </Row>

            {/* Expanded details */}
            {isOpen && (
              <div className="mt-3 border-t border-dashed border-gray-200 pt-1">
                <Row label="Username">{item.username || "-"}</Row>
                <Row label="Password">{item.password || "-"}</Row>
              </div>
            )}

            {/* Bottom-left actions */}
            <div className="absolute bottom-3 left-3 flex gap-3">
              {onView && (
                <button
                  type="button"
                  title="View"
                  aria-label="View"
                  className="rounded-full bg-blue-600 p-2 text-white hover:bg-blue-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(item);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}

              {onEdit && (
                <button
                  type="button"
                  title="Edit"
                  aria-label="Edit"
                  className="rounded-full border border-blue-300 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}
                >
                  <PenBox className="h-4 w-4" />
                </button>
              )}
            </div>

            <button
              type="button"
              aria-label={isOpen ? "Show less" : "Show more"}
              className="absolute bottom-3 right-3 rounded-full p-1 text-gray-600 hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              <ChevronDown
                className={`h-5 w-5 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}